import { useRef, useState, useCallback, useEffect } from 'react'
import { CAL_START_HOUR, CAL_END_HOUR, HOUR_HEIGHT } from './calendarConstants'
import { DRAG_THRESHOLD_PX, TOUCH_HOLD_MS, TOUCH_HOLD_TOLERANCE_PX } from '../../../shared/lib/schedule'

export type DragType = 'draft' | 'break' | 'tm'

export interface DragState {
  type: DragType
  /** Current hover day — changes on every cross-day move */
  dayIso: string
  /** Day where the drag started — stays constant throughout the drag */
  originDayIso: string
  entryId?: number
  /** Raw (unsnapped) top position in pixels — use this for visual placement during drag */
  topPx: number
  /** Snapped hours/minutes — used only on drop to determine the final time */
  hours: number
  minutes: number
  /**
   * `'armed'` — the hold finished but the finger has not moved yet; the block should show it has
   * been picked up. `'moving'` — it is actually following the pointer. A mouse drag never shows
   * `'armed'`: it has no hold, so the first state it ever reaches is `'moving'`.
   */
  phase: 'armed' | 'moving'
}

export interface TapInfo {
  type: DragType
  dayIso: string
  entryId?: number
  anchorRect: DOMRect
}

interface DragRef {
  type: DragType
  dayIso: string
  originDayIso: string
  entryId?: number
  anchorRect: DOMRect
  gridEl: HTMLElement
  offsetY: number
  startX: number
  startY: number
  moved: boolean
  /**
   * Touch only — starts `false` and flips once the hold completes. Until then this gesture is
   * still eligible to turn into an ordinary scroll: `handleMove` neither drags nor calls
   * `preventDefault`, so the browser is free to take it. A mouse press is armed immediately, since
   * a mouse has no scroll to lose the gesture to.
   */
  armed: boolean
  holdTimer: ReturnType<typeof setTimeout> | null
}

const MIN_MINUTES = CAL_START_HOUR * 60
const MAX_MINUTES = CAL_END_HOUR * 60 - 15

function snapToGrid(pointerYInGrid: number, offsetY: number) {
  const rawTopPx = pointerYInGrid - offsetY
  const minutesFromGridStart = (rawTopPx / HOUR_HEIGHT) * 60
  const totalMinutes = CAL_START_HOUR * 60 + minutesFromGridStart
  const rounded = Math.round(totalMinutes / 15) * 15
  const clamped = Math.max(MIN_MINUTES, Math.min(rounded, MAX_MINUTES))
  const minTopPx = 0
  const maxTopPx = ((MAX_MINUTES - MIN_MINUTES) / 60) * HOUR_HEIGHT
  const visualTopPx = Math.max(minTopPx, Math.min(rawTopPx, maxTopPx))
  return {
    topPx: visualTopPx,
    hours: Math.floor(clamped / 60),
    minutes: clamped % 60,
  }
}

export function useCalendarBlockDrag(
  onDrop: (type: DragType, dayIso: string, hours: number, minutes: number, entryId?: number, anchorRect?: DOMRect) => void,
  onTap?: (info: TapInfo) => void,
) {
  const dragRef = useRef<DragRef | null>(null)
  const lastSnapRef = useRef<{ hours: number; minutes: number } | null>(null)
  const [drag, setDrag] = useState<DragState | null>(null)

  /**
   * The pointer listeners are created once per gesture, inside `startDrag`, so without these they
   * would close over whichever `onDrop`/`onTap` existed the moment the finger went down. That is
   * not hypothetical: `useSchedule` polls every 60 seconds, and both callbacks are rebuilt from
   * the freshly fetched `technicianData`. A poll landing mid-drag left the drop running its
   * collision check — and its conflict popover — against a snapshot the user could no longer see.
   *
   * Same fix as `useBlockDrag`'s `commitRef` on the disposition board.
   */
  const dropRef = useRef(onDrop)
  const tapRef = useRef(onTap)
  useEffect(() => {
    dropRef.current = onDrop
    tapRef.current = onTap
  })

  const startDrag = useCallback(
    (
      type: DragType,
      dayIso: string,
      e: React.PointerEvent,
      gridEl: HTMLElement,
      blockTopPx: number,
      entryId?: number,
      anchorRect?: DOMRect,
    ) => {
      const isTouch = e.pointerType !== 'mouse'
      // A touch gesture must not claim the pointer yet — the hold below decides that. Calling
      // preventDefault on pointerdown itself can suppress the browser's own scroll handling for
      // the rest of the gesture, which would defeat the hold before it starts. A mouse has no
      // hold and needs this immediately, to block text selection.
      if (!isTouch) e.preventDefault()
      e.stopPropagation()

      const gridRect = gridEl.getBoundingClientRect()
      const pointerYInGrid = e.clientY - gridRect.top
      const offsetY = pointerYInGrid - blockTopPx

      const snapped = snapToGrid(pointerYInGrid, offsetY)
      lastSnapRef.current = { hours: snapped.hours, minutes: snapped.minutes }

      const session: DragRef = {
        type, dayIso, originDayIso: dayIso, entryId,
        anchorRect: anchorRect ?? (e.currentTarget as HTMLElement).getBoundingClientRect(),
        gridEl, offsetY,
        startX: e.clientX, startY: e.clientY, moved: false,
        armed: !isTouch,
        holdTimer: null,
      }
      dragRef.current = session

      /** Touch only: the hold completed — the block is picked up, still without having moved. */
      function arm() {
        if (dragRef.current !== session) return
        session.armed = true
        session.holdTimer = null
        document.body.style.userSelect = 'none'
        navigator.vibrate?.(15)
        setDrag({
          type: session.type,
          dayIso: session.dayIso,
          originDayIso: session.originDayIso,
          entryId: session.entryId,
          phase: 'armed',
          ...snapped,
        })
      }

      if (isTouch) {
        session.holdTimer = setTimeout(arm, TOUCH_HOLD_MS)
      } else {
        document.body.style.userSelect = 'none'
      }

      function handleMove(ev: PointerEvent) {
        if (!dragRef.current) return

        if (!dragRef.current.armed) {
          const dx = Math.abs(ev.clientX - dragRef.current.startX)
          const dy = Math.abs(ev.clientY - dragRef.current.startY)
          // The finger moved before the hold finished — a scroll, not a hold. Abort the whole
          // gesture and leave the browser to it: no preventDefault was ever called, so the scroll
          // that is already under way keeps going. Neither onDrop nor onTap fires for this one.
          if (dx > TOUCH_HOLD_TOLERANCE_PX || dy > TOUCH_HOLD_TOLERANCE_PX) cleanup()
          return
        }

        ev.preventDefault()

        if (!dragRef.current.moved) {
          const dx = Math.abs(ev.clientX - dragRef.current.startX)
          const dy = Math.abs(ev.clientY - dragRef.current.startY)
          if (dx < DRAG_THRESHOLD_PX && dy < DRAG_THRESHOLD_PX) return
          dragRef.current.moved = true
        }

        const elUnder = document.elementFromPoint(ev.clientX, ev.clientY)
        const hoveredGrid = elUnder?.closest<HTMLElement>('[data-planning-grid]')
        if (hoveredGrid) {
          dragRef.current.dayIso = hoveredGrid.dataset.dayIso!
          dragRef.current.gridEl = hoveredGrid
        }

        const rect = dragRef.current.gridEl.getBoundingClientRect()
        const yInGrid = ev.clientY - rect.top
        const snap = snapToGrid(yInGrid, dragRef.current.offsetY)
        lastSnapRef.current = { hours: snap.hours, minutes: snap.minutes }
        setDrag({
          type: dragRef.current.type,
          dayIso: dragRef.current.dayIso,
          originDayIso: dragRef.current.originDayIso,
          entryId: dragRef.current.entryId,
          phase: 'moving',
          ...snap,
        })
      }

      function handleUp() {
        const snap = lastSnapRef.current
        const ref = dragRef.current
        cleanup()
        if (!ref) return
        // `moved` can only be true once armed, so an unmoved release — whether the hold never
        // finished or it finished and the finger simply lifted — is a tap either way.
        if (ref.moved && snap) {
          dropRef.current(ref.type, ref.dayIso, snap.hours, snap.minutes, ref.entryId, ref.anchorRect)
        } else {
          tapRef.current?.({ type: ref.type, dayIso: ref.dayIso, entryId: ref.entryId, anchorRect: ref.anchorRect })
        }
      }

      function cleanup() {
        if (dragRef.current?.holdTimer) clearTimeout(dragRef.current.holdTimer)
        dragRef.current = null
        lastSnapRef.current = null
        setDrag(null)
        document.removeEventListener('pointermove', handleMove)
        document.removeEventListener('pointerup', handleUp)
        document.removeEventListener('pointercancel', cleanup)
        document.body.style.userSelect = ''
      }

      document.addEventListener('pointermove', handleMove)
      document.addEventListener('pointerup', handleUp)
      document.addEventListener('pointercancel', cleanup)
    },
    // Empty on purpose: both callbacks are reached through the refs above, so `startDrag` keeps a
    // stable identity and the pointer-down handlers built from it do not churn every poll.
    [],
  )

  return { drag, startDrag }
}
